"use client";

import { Controller } from "react-hook-form";
import {
  packagingMaterials,
  serviceOptions,
  type WasteAuditFormValues,
} from "@/lib/validation/schemas";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import type { UseFormReturn } from "react-hook-form";

const inputBaseStyles =
  "mt-1 w-full rounded-md border-2 px-4 py-3 outline-none placeholder:text-muted-foreground focus-visible:ring-[2px] focus-visible:ring-ring/20";
const inputValidStyles = "border-border focus-visible:border-ring";
const inputErrorStyles =
  "border-destructive focus:border-destructive focus-visible:ring-destructive";

interface WasteAuditFormFieldsProps {
  form: UseFormReturn<WasteAuditFormValues>;
  onSubmit: (values: WasteAuditFormValues) => Promise<void>;
}

export function WasteAuditFormFields({
  form,
  onSubmit,
}: WasteAuditFormFieldsProps) {
  const { register, control, handleSubmit, formState } = form;
  const { errors, isSubmitting } = formState;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <BotcheckField control={control} />

      <div className="grid gap-5">
        <WasteAuditContactFields errors={errors} register={register} />

        <WasteAuditReachFields errors={errors} register={register} />

        <WasteAuditServiceField errors={errors} register={register} />

        <PackagingMaterialsField control={control} errors={errors} />

        <WasteAuditTextField
          id="estimatedVolume"
          label="Estimated Yearly Volume"
          placeholder="e.g., 10000 units"
          hasError={!!errors.estimatedVolume}
          errorMessage={errors.estimatedVolume?.message}
          register={register}
        />

        <AdditionalInfoField register={register} />
      </div>

      <div className="mt-6">
        <Button variant="primary"
          type="submit"
          disabled={isSubmitting}
          size="default"
          className="w-full py-4"
        >
          {isSubmitting ? (
            <Spinner className="mx-auto size-5" />
          ) : (
            "Submit Request"
          )}
        </Button>
      </div>
    </form>
  );
}

function BotcheckField({
  control,
}: {
  control: UseFormReturn<WasteAuditFormValues>["control"];
}) {
  return (
    <Controller
      name="botcheck"
      control={control}
      render={({ field }) => (
        <input
          type="checkbox"
          className="hidden"
          style={{ display: "none" }}
          checked={field.value ?? false}
          onChange={(e) => field.onChange(e.target.checked)}
        />
      )}
    />
  );
}

function WasteAuditContactFields({
  errors,
  register,
}: {
  errors: UseFormReturn<WasteAuditFormValues>["formState"]["errors"];
  register: UseFormReturn<WasteAuditFormValues>["register"];
}) {
  return (
    <>
      <WasteAuditTextField
        id="company"
        label="Company Name"
        placeholder="Your Company Name"
        hasError={!!errors.company}
        errorMessage={errors.company?.message}
        register={register}
      />

      <div className="grid gap-5 lg:grid-cols-2">
        <WasteAuditTextField
          id="firstName"
          label="First Name"
          placeholder="First Name"
          hasError={!!errors.firstName}
          errorMessage={errors.firstName?.message}
          register={register}
        />
        <WasteAuditTextField
          id="lastName"
          label="Last Name"
          placeholder="Last Name"
          hasError={!!errors.lastName}
          errorMessage={errors.lastName?.message}
          register={register}
        />
      </div>
    </>
  );
}

function WasteAuditReachFields({
  errors,
  register,
}: {
  errors: UseFormReturn<WasteAuditFormValues>["formState"]["errors"];
  register: UseFormReturn<WasteAuditFormValues>["register"];
}) {
  return (    <div className="grid gap-5 lg:grid-cols-2">
      <WasteAuditTextField
        id="email"
        label="Email Address"
        placeholder="you@company.com"
        type="email"
        hasError={!!errors.email}
        errorMessage={errors.email?.message}
        register={register}
      />
      <WasteAuditTextField
        id="phone"
        label="Phone Number"
        placeholder="123-456-7890"
        type="tel"
        hasError={!!errors.phone}
        errorMessage={errors.phone?.message}
        register={register}
      />
    </div>
  );
}

function AdditionalInfoField({
  register,
}: {
  register: UseFormReturn<WasteAuditFormValues>["register"];
}) {
  return (
    <div>
      <label
        htmlFor="additionalInfo"
        className="text-sm font-medium text-foreground"
      >
        Additional Information
      </label>
      <textarea
        id="additionalInfo"
        placeholder="Provide any additional information here..."
        {...register("additionalInfo")}
        className="mt-1 w-full rounded-md border-2 px-4 py-3 outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/20 focus-visible:ring-[2px]"
      />
    </div>
  );
}

function WasteAuditTextField({
  id,
  label,
  placeholder,
  type = "text",
  hasError,
  errorMessage,
  register,
}: {
  id: keyof WasteAuditFormValues;
  label: string;
  placeholder: string;
  type?: string;
  hasError: boolean;
  errorMessage?: string;
  register: UseFormReturn<WasteAuditFormValues>["register"];
}) {
  return (
    <div>
      <label htmlFor={id} className="text-sm font-medium text-foreground">
        {label}
      </label>
      <input
        type={type}
        id={id}
        placeholder={placeholder}
        {...register(id)}
        className={cn(
          inputBaseStyles,
          hasError ? inputErrorStyles : inputValidStyles,
        )}
      />
      {errorMessage && (
        <div className="mt-1 text-destructive-strong">
          <small>{errorMessage}</small>
        </div>
      )}
    </div>
  );
}

function WasteAuditServiceField({
  errors,
  register,
}: {
  errors: UseFormReturn<WasteAuditFormValues>["formState"]["errors"];
  register: UseFormReturn<WasteAuditFormValues>["register"];
}) {
  return (
    <div>
      <label htmlFor="service" className="text-sm font-medium text-foreground">
        Service
      </label>
      <select
        id="service"
        {...register("service")}
        className={cn(
          inputBaseStyles,
          errors.service ? inputErrorStyles : inputValidStyles,
        )}
      >
        <option value="">Select a service</option>
        {serviceOptions.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      {errors.service?.message && (
        <div className="mt-1 text-destructive-strong">
          <small>{errors.service.message}</small>
        </div>
      )}
    </div>
  );
}

function PackagingMaterialsField({
  control,
  errors,
}: {
  control: UseFormReturn<WasteAuditFormValues>["control"];
  errors: UseFormReturn<WasteAuditFormValues>["formState"]["errors"];
}) {
  return (
    <Controller
      name="packagingMaterials"
      control={control}
      render={({ field }) => (
        <fieldset>
          <legend className="text-sm font-medium text-foreground">
            Packaging Materials (Select all that apply)
          </legend>
          <div className="flex flex-col space-y-2 mt-2">
            {packagingMaterials.map((material) => (
              <label key={material} className="inline-flex items-center">
                <input
                  type="checkbox"
                  value={material}
                  className="form-checkbox"
                  checked={(field.value ?? []).includes(material)}
                  onChange={(e) => {
                    const currentValues = field.value ?? [];
                    if (e.target.checked) {
                      field.onChange([...currentValues, material]);
                    } else {
                      field.onChange(
                        currentValues.filter((v) => v !== material),
                      );
                    }
                  }}
                />
                <span className="ml-2 text-foreground">{material}</span>
              </label>
            ))}
          </div>
          {errors.packagingMaterials?.message && (
            <div className="mt-1 text-destructive-strong">
              <small>{errors.packagingMaterials.message}</small>
            </div>
          )}
        </fieldset>
      )}
    />
  );
}
